/**
 * 展示层格式化：手机号掩码、时间。
 *
 * 这里**没有** token 数量的格式化器：v1.3.0 起前端拿不到绝对 token 量，
 * 额度一律走服务端算好的百分比（见 `quota-display.ts`），
 * 消耗明细走服务端算好的占比。留一个没人调用的数字缩写器只会在
 * 下一次有人想"顺手显示一下用量"时被重新用上。
 */

/** 手机号掩码；没有手机号时返回中文占位（微信登录用户 `phone === null`）。 */
export function maskPhone(phone: string | null): string {
  if (phone === null || phone.length === 0) {
    return '未绑定手机号';
  }
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 7) {
    return phone;
  }
  const head = digits.slice(-11, -8);
  const tail = digits.slice(-4);
  return head.length === 3 ? `${head}****${tail}` : `****${tail}`;
}

/** ISO 时间 → `YYYY-MM-DD HH:mm`（本地时区）；无法解析时原样返回。 */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const RELATIVE_UNITS: ReadonlyArray<{ limitSeconds: number; divisor: number; suffix: string }> = [
  { limitSeconds: 60, divisor: 1, suffix: '秒前' },
  { limitSeconds: 3600, divisor: 60, suffix: '分钟前' },
  { limitSeconds: 86_400, divisor: 3600, suffix: '小时前' },
  { limitSeconds: 2_592_000, divisor: 86_400, suffix: '天前' },
];

/**
 * 相对时间。10 秒内统一说「刚刚」——比「3 秒前」更像人说的话；
 * 超过 30 天退回绝对日期，因为「45 天前」没人换算得过来。
 */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) {
    return iso;
  }
  const elapsedSeconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (elapsedSeconds < 10) {
    return '刚刚';
  }
  for (const unit of RELATIVE_UNITS) {
    if (elapsedSeconds < unit.limitSeconds) {
      return `${Math.floor(elapsedSeconds / unit.divisor)}${unit.suffix}`;
    }
  }
  return formatDateTime(iso).slice(0, 10);
}
